import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import LoginPage from "./pages/LoginPage";
import LandingPage from "./pages/landing/LandingPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import SetPasswordPage from "./pages/SetPasswordPage";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import AppShell, { AppHome } from "./pages/AppShell";
import PortalShell, { PortalHome, PortalPanoramica, PortalTicketList, PortalTicketNew, PortalTicketDetail } from "./pages/PortalShell";
import ClientList from "./pages/clients/ClientList";
import ClientNew from "./pages/clients/ClientNew";
import ClientDetail from "./pages/clients/ClientDetail";
import TicketList from "./pages/tickets/TicketList";
import TicketNew from "./pages/tickets/TicketNew";
import TicketDetail from "./pages/tickets/TicketDetail";
import Comunicazioni from "./pages/comunicazioni/Comunicazioni";
import PortalComunicazioni from "./pages/comunicazioni/PortalComunicazioni";
import Scadenze from "./pages/scadenze/Scadenze";
import PortalScadenze from "./pages/scadenze/PortalScadenze";
import Documenti from "./pages/documenti/Documenti";
import PortalDocumenti from "./pages/documenti/PortalDocumenti";
import Riunioni from "./pages/riunioni/Riunioni";
import PortalRiunioni from "./pages/riunioni/PortalRiunioni";
import RiunioneJoin from "./pages/riunioni/RiunioneJoin";
import RiunioneDetail from "./pages/riunioni/RiunioneDetail";
import { ChecklistList, ChecklistDetail } from "./pages/checklist/Checklist";
import { PortalChecklistList, PortalChecklistDetail } from "./pages/checklist/PortalChecklist";
import Spese from "./pages/spese/Spese";
import ProfilePage from "./pages/ProfilePage";
import AdminConversations from "./pages/admin/AdminConversations";
import AdminConversationDetail from "./pages/admin/AdminConversationDetail";
import AdminAccessLog from "./pages/admin/AdminAccessLog";
import Impostazioni from "./pages/impostazioni/Impostazioni";

function FullScreenLoader() {
    return (
        <div className="min-h-screen w-full flex items-center justify-center" style={{ background: "#15181C" }} data-testid="app-loader">
            <div style={{ color: "#6B7178", fontSize: "13px" }}>Caricamento…</div>
        </div>
    );
}

function ProtectedRoute({ children, allowOnlyMustChange = false, requireType = null }) {
    const { user, loading } = useAuth();
    const location = useLocation();
    if (loading) return <FullScreenLoader />;
    if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
    if (user.must_change_password && !allowOnlyMustChange) return <Navigate to="/change-password" replace />;
    if (requireType && user.user_type !== requireType) return <Navigate to="/" replace />;
    return children;
}

function RootRedirect() {
    const { user, loading } = useAuth();
    if (loading) return <FullScreenLoader />;
    if (!user) return <LandingPage />;
    if (user.must_change_password) return <Navigate to="/change-password" replace />;
    return <Navigate to={user.user_type === "internal" ? "/app" : "/portal"} replace />;
}

function PublicOnly({ children }) {
    const { user, loading } = useAuth();
    if (loading) return <FullScreenLoader />;
    if (user) {
        if (user.must_change_password) return <Navigate to="/change-password" replace />;
        return <Navigate to={user.user_type === "internal" ? "/app" : "/portal"} replace />;
    }
    return children;
}

export default function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <Toaster position="bottom-right" duration={3500} theme="light" closeButton={false} richColors={false} />
                <Routes>
                    <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
                    <Route path="/forgot-password" element={<PublicOnly><ForgotPasswordPage /></PublicOnly>} />
                    <Route path="/set-password" element={<SetPasswordPage mode="set" />} />
                    <Route path="/reset-password" element={<SetPasswordPage mode="reset" />} />
                    <Route path="/change-password" element={<ProtectedRoute allowOnlyMustChange><ChangePasswordPage /></ProtectedRoute>} />
                    <Route path="/app" element={<ProtectedRoute requireType="internal"><AppShell /></ProtectedRoute>}>
                        <Route index element={<AppHome />} />
                        <Route path="clienti" element={<ClientList />} />
                        <Route path="clienti/nuova" element={<ClientNew />} />
                        <Route path="clienti/:id" element={<ClientDetail />} />
                        <Route path="ticket" element={<TicketList />} />
                        <Route path="ticket/nuovo" element={<TicketNew />} />
                        <Route path="ticket/:id" element={<TicketDetail />} />
                        <Route path="comunicazioni" element={<Comunicazioni />} />
                        <Route path="scadenze" element={<Scadenze />} />
                        <Route path="riunioni" element={<Riunioni />} />
                        <Route path="riunioni/:id" element={<RiunioneDetail />} />
                        <Route path="riunioni/:id/join" element={<RiunioneJoin userType="internal" />} />
                        <Route path="documenti" element={<Documenti />} />
                        <Route path="checklist" element={<ChecklistList />} />
                        <Route path="checklist/:id" element={<ChecklistDetail />} />
                        <Route path="spese" element={<Spese />} />
                        <Route path="profilo" element={<ProfilePage />} />
                        <Route path="impostazioni" element={<Impostazioni />} />
                        <Route path="admin/conversazioni" element={<AdminConversations />} />
                        <Route path="admin/conversazioni/:conv_type/:conv_id" element={<AdminConversationDetail />} />
                        <Route path="admin/audit-access" element={<AdminAccessLog />} />
                    </Route>
                    <Route path="/portal" element={<ProtectedRoute requireType="client"><PortalShell /></ProtectedRoute>}>
                        <Route index element={<PortalHome />} />
                        <Route path="panoramica" element={<PortalPanoramica />} />
                        <Route path="comunicazioni" element={<PortalComunicazioni />} />
                        <Route path="ticket" element={<PortalTicketList />} />
                        <Route path="ticket/nuovo" element={<PortalTicketNew />} />
                        <Route path="ticket/:id" element={<PortalTicketDetail />} />
                        <Route path="scadenze" element={<PortalScadenze />} />
                        <Route path="riunioni" element={<PortalRiunioni />} />
                        <Route path="riunioni/:id/join" element={<RiunioneJoin userType="client" />} />
                        <Route path="documenti" element={<PortalDocumenti />} />
                        <Route path="profilo" element={<ProfilePage />} />
                        <Route path="checklist" element={<PortalChecklistList />} />
                        <Route path="checklist/:id" element={<PortalChecklistDetail />} />
                    </Route>
                    <Route path="/" element={<RootRedirect />} />
                    <Route path="*" element={<RootRedirect />} />
                </Routes>
            </BrowserRouter>
        </AuthProvider>
    );
}
